import React, { useState, useEffect, useCallback } from 'react';
import {
  Card, Table, Button, Modal, Form, Input, Select, DatePicker, Switch,
  InputNumber, Space, Tag, Tabs, Row, Col, Statistic, message, Popconfirm,
  Typography, Timeline, Progress, Badge, Descriptions, Empty,
} from 'antd';
import {
  ScheduleOutlined, PlusOutlined, PlayCircleOutlined, DeleteOutlined,
  EditOutlined, FacebookOutlined, MailOutlined, FileTextOutlined,
  VideoCameraOutlined, RobotOutlined, ReloadOutlined, ClockCircleOutlined,
  ThunderboltOutlined, BulbOutlined, UnorderedListOutlined,
  CheckCircleOutlined, CloseCircleOutlined, SyncOutlined,
  LoadingOutlined, ExclamationCircleOutlined, InfoCircleOutlined,
} from '@ant-design/icons';
import { schedulerAPI, videoAPI } from '../services/api';
import dayjs from 'dayjs';

const { Title, Text, Paragraph } = Typography;
const { TextArea } = Input;

const CONTENT_TYPES = [
  { value: 'blog_post', label: 'Blog Post', icon: <FileTextOutlined /> },
  { value: 'ai_article', label: 'AI Article', icon: <RobotOutlined /> },
  { value: 'ai_video', label: 'AI Video', icon: <VideoCameraOutlined /> },
  { value: 'email', label: 'Email', icon: <MailOutlined /> },
];

const SCHEDULE_TYPES = [
  { value: 'once', label: 'One-time' },
  { value: 'daily', label: 'Daily' },
  { value: 'weekly', label: 'Weekly' },
  { value: 'monthly', label: 'Monthly' },
];

const STATUS_COLORS = {
  draft: 'default', scheduled: 'blue', running: 'orange',
  completed: 'green', failed: 'red', paused: 'cyan',
};

const EXEC_STATUS_COLORS = {
  running: 'processing', success: 'success', partial: 'warning', failed: 'error',
};

const STEP_LABELS = {
  generate_content: '產生內容',
  generate_video: '生成影片',
  publish_blog: '發佈部落格',
  send_email: '發送 Email',
  post_facebook: '發佈 Facebook',
  create_fb_ad: '建立 FB 廣告',
  done: '完成',
  error: '錯誤',
  initializing: '初始化',
};

const DAYS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

export default function MarketingSchedulerPage() {
  const [schedules, setSchedules] = useState([]);
  const [loading, setLoading] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [historyModal, setHistoryModal] = useState(null);
  const [history, setHistory] = useState([]);
  const [aiModal, setAiModal] = useState(false);
  const [aiLoading, setAiLoading] = useState(false);
  const [aiSuggestion, setAiSuggestion] = useState('');
  const [activeTab, setActiveTab] = useState('schedules');
  // Logs state
  const [logs, setLogs] = useState([]);
  const [logsTotal, setLogsTotal] = useState(0);
  const [logsPage, setLogsPage] = useState(1);
  const [logsLoading, setLogsLoading] = useState(false);
  const [logFilter, setLogFilter] = useState(null);
  // Running / progress state
  const [runningExecs, setRunningExecs] = useState([]);
  const [detailModal, setDetailModal] = useState(null);
  // Video config options
  const [sourceVideos, setSourceVideos] = useState([]);
  const [voices, setVoices] = useState([]);

  const [form] = Form.useForm();
  const [aiForm] = Form.useForm();

  const fetchSchedules = useCallback(async () => {
    setLoading(true);
    try {
      const res = await schedulerAPI.list();
      setSchedules(res.data || []);
    } catch { /* ignore */ }
    setLoading(false);
  }, []);

  const fetchLogs = useCallback(async (page = 1, status = null) => {
    setLogsLoading(true);
    try {
      const params = { limit: 20, skip: (page - 1) * 20 };
      if (status) params.status = status;
      const res = await schedulerAPI.logs(params);
      setLogs(res.data?.items || []);
      setLogsTotal(res.data?.total || 0);
    } catch { /* ignore */ }
    setLogsLoading(false);
  }, []);

  const fetchRunning = useCallback(async () => {
    try {
      const res = await schedulerAPI.running();
      setRunningExecs(res.data || []);
    } catch { /* ignore */ }
  }, []);

  useEffect(() => { fetchSchedules(); }, [fetchSchedules]);

  useEffect(() => {
    videoAPI.getSourceVideos({ limit: 100 }).then(res => setSourceVideos(res.data?.data || [])).catch(() => {});
    videoAPI.getVoices().then(res => setVoices(res.data || [])).catch(() => {});
  }, []);

  // Load logs and running on tab switch or page/filter change (no auto-polling)
  useEffect(() => {
    if (activeTab === 'logs') {
      fetchLogs(logsPage, logFilter);
      fetchRunning();
    }
  }, [activeTab, logsPage, logFilter, fetchLogs, fetchRunning]);

  const handleSave = async () => {
    try {
      const values = await form.validateFields();
      // Extra validation for ai_video
      if (values.content_type === 'ai_video' && (!values.source_video_id || !values.video_text?.trim())) {
        message.error('AI Video 需要選擇 Source Video 並輸入 Script Text');
        return;
      }
      if (values.scheduled_at) values.scheduled_at = values.scheduled_at.toISOString();
      // Pack content-type-specific fields into content_config
      const configFields = ['source_video_id', 'video_text', 'video_voice', 'ai_prompt', 'email_subject'];
      const config = {};
      configFields.forEach(f => {
        if (values[f] != null && values[f] !== '') {
          const key = f === 'video_text' ? 'text' : f === 'video_voice' ? 'voice' : f === 'ai_prompt' ? 'prompt' : f === 'email_subject' ? 'subject' : f;
          config[key] = values[f];
          delete values[f];
        }
      });
      if (Object.keys(config).length > 0) values.content_config = config;
      if (editingId) {
        await schedulerAPI.update(editingId, values);
        message.success('Schedule updated');
      } else {
        await schedulerAPI.create(values);
        message.success('Schedule created');
      }
      setModalOpen(false);
      form.resetFields();
      setEditingId(null);
      fetchSchedules();
    } catch (e) { if (e.message) message.error(e.message); }
  };

  const handleEdit = (record) => {
    setEditingId(record.id);
    const vals = { ...record };
    if (vals.scheduled_at) vals.scheduled_at = dayjs(vals.scheduled_at);
    // Unpack content_config into form fields
    const cfg = vals.content_config || {};
    if (cfg.source_video_id) vals.source_video_id = cfg.source_video_id;
    if (cfg.text) vals.video_text = cfg.text;
    if (cfg.voice) vals.video_voice = cfg.voice;
    if (cfg.prompt) vals.ai_prompt = cfg.prompt;
    if (cfg.subject) vals.email_subject = cfg.subject;
    form.setFieldsValue(vals);
    setModalOpen(true);
  };

  const handleDelete = async (id) => {
    await schedulerAPI.delete(id);
    message.success('Deleted');
    fetchSchedules();
  };

  const handleExecute = async (id) => {
    try {
      await schedulerAPI.execute(id);
      message.success('Execution started');
      fetchSchedules();
      fetchRunning();
      setActiveTab('logs');
    } catch { message.error('Failed'); }
  };

  const showHistory = async (id) => {
    try {
      const res = await schedulerAPI.history(id);
      setHistory(res.data || []);
      setHistoryModal(id);
    } catch { message.error('Failed to load history'); }
  };

  const showDetail = async (execId) => {
    try {
      const res = await schedulerAPI.getExecution(execId);
      setDetailModal(res.data || null);
    } catch { message.error('Failed to load details'); }
  };

  const handleAiSuggest = async () => {
    try {
      const values = await aiForm.validateFields();
      setAiLoading(true);
      const res = await schedulerAPI.aiSuggest(values);
      setAiSuggestion(res.data?.suggestion || 'No suggestion');
    } catch { /* ignore */ }
    setAiLoading(false);
  };

  const stats = {
    total: schedules.length,
    scheduled: schedules.filter(s => s.status === 'scheduled').length,
    completed: schedules.filter(s => s.status === 'completed').length,
    running: schedules.filter(s => s.status === 'running').length,
  };

  const columns = [
    { title: 'Name', dataIndex: 'name', width: 200, ellipsis: true },
    {
      title: 'Type', dataIndex: 'content_type', width: 110,
      render: t => {
        const ct = CONTENT_TYPES.find(c => c.value === t);
        return ct ? <Space size={4}>{ct.icon}<span>{ct.label}</span></Space> : t;
      },
    },
    {
      title: 'Schedule', dataIndex: 'schedule_type', width: 100,
      render: (t, r) => {
        let label = t;
        if (t === 'weekly') label = `Weekly (${DAYS[r.cron_day_of_week || 0]})`;
        if (t === 'monthly') label = `Monthly (${r.cron_day_of_month || 1}th)`;
        return <Tag icon={<ClockCircleOutlined />}>{label}</Tag>;
      },
    },
    {
      title: 'Channels', dataIndex: 'channels', width: 140,
      render: chs => (chs || []).map(c => {
        const icons = { facebook: <FacebookOutlined />, email: <MailOutlined />, blog: <FileTextOutlined /> };
        return <Tag key={c} icon={icons[c]}>{c}</Tag>;
      }),
    },
    {
      title: 'FB Ad', width: 80, align: 'center',
      render: (_, r) => r.fb_ad_enabled ? <Tag color="blue">NT${r.fb_ad_budget || 0}</Tag> : '-',
    },
    {
      title: 'Status', dataIndex: 'status', width: 100,
      render: s => (
        <Tag color={STATUS_COLORS[s] || 'default'}
          icon={s === 'running' ? <SyncOutlined spin /> : undefined}>
          {s}
        </Tag>
      ),
    },
    {
      title: 'Next Run', dataIndex: 'next_run_at', width: 160,
      render: v => v ? dayjs(v).format('MM/DD HH:mm') : '-',
    },
    { title: 'Runs', dataIndex: 'run_count', width: 60, align: 'center' },
    {
      title: 'Actions', width: 180, fixed: 'right',
      render: (_, r) => (
        <Space size="small">
          <Button size="small" icon={<PlayCircleOutlined />} type="primary"
            onClick={() => handleExecute(r.id)} loading={r.status === 'running'}>Run</Button>
          <Button size="small" icon={<ClockCircleOutlined />} onClick={() => showHistory(r.id)} />
          <Button size="small" icon={<EditOutlined />} onClick={() => handleEdit(r)} />
          <Popconfirm title="Delete?" onConfirm={() => handleDelete(r.id)}>
            <Button size="small" icon={<DeleteOutlined />} danger />
          </Popconfirm>
        </Space>
      ),
    },
  ];

  const scheduleType = Form.useWatch('schedule_type', form);
  const contentType = Form.useWatch('content_type', form);

  // ── Running Progress Cards ──
  const RunningPanel = () => {
    if (runningExecs.length === 0) return null;
    return (
      <div style={{ marginBottom: 16 }}>
        {runningExecs.map(exec => {
          const prog = exec.progress || {};
          const steps = prog.steps || [];
          const elapsed = exec.duration_ms ? (exec.duration_ms / 1000).toFixed(1) : '...';
          return (
            <Card key={exec.id} size="small" style={{ marginBottom: 8, borderLeft: '4px solid #fa8c16' }}>
              <Row gutter={16} align="middle">
                <Col flex="auto">
                  <Space>
                    <SyncOutlined spin style={{ color: '#fa8c16', fontSize: 18 }} />
                    <div>
                      <Text strong>{exec.schedule_name}</Text>
                      <Text type="secondary" style={{ marginLeft: 8 }}>
                        {STEP_LABELS[prog.current_step] || prog.current_step || 'Starting...'}
                      </Text>
                    </div>
                  </Space>
                  <Progress percent={prog.pct || 0} size="small" status="active"
                    style={{ marginTop: 4, marginBottom: 0 }} />
                  <Space size={4} wrap style={{ marginTop: 4 }}>
                    {steps.map((s, i) => (
                      <Tag key={i} color={s.status === 'ok' ? 'green' : s.status === 'fail' ? 'red' : 'blue'}
                        icon={s.status === 'ok' ? <CheckCircleOutlined /> : s.status === 'fail' ? <CloseCircleOutlined /> : <SyncOutlined spin />}>
                        {STEP_LABELS[s.name] || s.name}
                      </Tag>
                    ))}
                  </Space>
                </Col>
                <Col>
                  <Text type="secondary">{elapsed}s</Text>
                </Col>
              </Row>
            </Card>
          );
        })}
      </div>
    );
  };

  // ── Log Status Tag ──
  const ExecStatusTag = ({ status }) => {
    const map = {
      running: { color: 'processing', icon: <SyncOutlined spin />, text: 'Running' },
      success: { color: 'success', icon: <CheckCircleOutlined />, text: 'Success' },
      partial: { color: 'warning', icon: <ExclamationCircleOutlined />, text: 'Partial' },
      failed: { color: 'error', icon: <CloseCircleOutlined />, text: 'Failed' },
    };
    const m = map[status] || { color: 'default', text: status };
    return <Badge status={m.color} text={m.text} />;
  };

  // ── Logs columns ──
  const logColumns = [
    {
      title: 'Time', dataIndex: 'executed_at', width: 150,
      render: v => v ? dayjs(v).format('MM/DD HH:mm:ss') : '-',
    },
    {
      title: 'Schedule', dataIndex: 'schedule_name', width: 200, ellipsis: true,
    },
    {
      title: 'Type', dataIndex: 'content_type', width: 100,
      render: t => {
        const ct = CONTENT_TYPES.find(c => c.value === t);
        return ct ? <Space size={4}>{ct.icon}<span>{ct.label}</span></Space> : t;
      },
    },
    {
      title: 'Status', dataIndex: 'status', width: 120,
      render: s => <ExecStatusTag status={s} />,
    },
    {
      title: 'Progress', width: 160,
      render: (_, r) => {
        const pct = r.progress?.pct || (r.status === 'success' ? 100 : 0);
        return <Progress percent={pct} size="small"
          status={r.status === 'running' ? 'active' : r.status === 'failed' ? 'exception' : 'normal'} />;
      },
    },
    {
      title: 'Duration', dataIndex: 'duration_ms', width: 100, align: 'right',
      render: v => v != null ? `${(v / 1000).toFixed(1)}s` : '-',
    },
    {
      title: 'Results', width: 200,
      render: (_, r) => {
        if (r.error) return <Text type="danger" ellipsis style={{ maxWidth: 180 }}>{r.error}</Text>;
        if (!r.results) return '-';
        return (
          <Space size={4} wrap>
            {Object.entries(r.results).map(([k, v]) => (
              <Tag key={k} color={v?.ok ? 'green' : 'red'}>
                {k}: {v?.ok ? 'OK' : (v?.error || 'Fail').substring(0, 20)}
              </Tag>
            ))}
          </Space>
        );
      },
    },
    {
      title: '', width: 80, fixed: 'right',
      render: (_, r) => (
        <Button size="small" icon={<InfoCircleOutlined />} onClick={() => showDetail(r.id)}>
          Detail
        </Button>
      ),
    },
  ];

  // ── Tab items ──
  const tabItems = [
    {
      key: 'schedules',
      label: <span><ScheduleOutlined /> Schedules</span>,
      children: (
        <>
          <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 16, flexWrap: 'wrap', gap: 8 }}>
            <Space>
              <Button icon={<ReloadOutlined />} onClick={fetchSchedules}>Refresh</Button>
              <Button icon={<BulbOutlined />} onClick={() => { setAiModal(true); setAiSuggestion(''); }}>AI Suggest</Button>
            </Space>
            <Button type="primary" icon={<PlusOutlined />}
              onClick={() => { setEditingId(null); form.resetFields(); setModalOpen(true); }}>
              New Schedule
            </Button>
          </div>
          <Table columns={columns} dataSource={schedules} rowKey="id" loading={loading}
            size="middle" pagination={{ pageSize: 20 }} scroll={{ x: 1200 }} />
        </>
      ),
    },
    {
      key: 'logs',
      label: (
        <span>
          <UnorderedListOutlined /> Execution Logs
          {runningExecs.length > 0 && (
            <Badge count={runningExecs.length} style={{ marginLeft: 6 }}
              status="processing" />
          )}
        </span>
      ),
      children: (
        <>
          <RunningPanel />
          <div style={{ marginBottom: 12 }}>
            <Space>
              <Select value={logFilter} onChange={v => { setLogFilter(v); setLogsPage(1); }}
                allowClear placeholder="Filter status" style={{ width: 140 }}
                options={[
                  { value: 'running', label: 'Running' },
                  { value: 'success', label: 'Success' },
                  { value: 'partial', label: 'Partial' },
                  { value: 'failed', label: 'Failed' },
                ]} />
              <Button icon={<ReloadOutlined />} onClick={() => { fetchLogs(logsPage, logFilter); fetchRunning(); fetchSchedules(); }}>
                Refresh
              </Button>
            </Space>
          </div>
          <Table columns={logColumns} dataSource={logs} rowKey="id" loading={logsLoading}
            size="middle" scroll={{ x: 1100 }}
            pagination={{
              current: logsPage, total: logsTotal, pageSize: 20,
              onChange: p => setLogsPage(p), showTotal: t => `Total ${t}`,
            }} />
        </>
      ),
    },
  ];

  return (
    <div>
      <Card style={{ marginBottom: 16 }} styles={{ body: { padding: 0 } }}>
        <div style={{
          background: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)',
          padding: '24px 32px', borderRadius: '8px 8px 0 0',
        }}>
          <Space align="center" size={16}>
            <ScheduleOutlined style={{ fontSize: 32, color: '#fff' }} />
            <div>
              <Title level={3} style={{ margin: 0, color: '#fff' }}>Marketing Scheduler</Title>
              <Text style={{ color: 'rgba(255,255,255,0.85)' }}>AI-powered content scheduling & multi-channel publishing</Text>
            </div>
          </Space>
        </div>
      </Card>

      <Row gutter={[16, 16]} style={{ marginBottom: 16 }}>
        <Col xs={12} sm={6}><Card><Statistic title="Total Schedules" value={stats.total} prefix={<ScheduleOutlined />} /></Card></Col>
        <Col xs={12} sm={6}><Card><Statistic title="Scheduled" value={stats.scheduled} valueStyle={{ color: '#1890ff' }} /></Card></Col>
        <Col xs={12} sm={6}>
          <Card>
            <Statistic title="Running" value={stats.running} valueStyle={{ color: '#fa8c16' }}
              prefix={stats.running > 0 ? <SyncOutlined spin /> : undefined} />
          </Card>
        </Col>
        <Col xs={12} sm={6}><Card><Statistic title="Completed" value={stats.completed} valueStyle={{ color: '#52c41a' }} /></Card></Col>
      </Row>

      <Card>
        <Tabs activeKey={activeTab} onChange={setActiveTab} items={tabItems} />
      </Card>

      {/* Create/Edit Modal */}
      <Modal title={editingId ? 'Edit Schedule' : 'New Schedule'} open={modalOpen}
        onOk={handleSave} onCancel={() => { setModalOpen(false); setEditingId(null); }}
        width={640} forceRender>
        <Form form={form} layout="vertical" initialValues={{ schedule_type: 'once', channels: [], cron_hour: 9, cron_minute: 0 }}>
          <Form.Item name="name" label="Name" rules={[{ required: true }]}><Input /></Form.Item>
          <Form.Item name="description" label="Description"><TextArea rows={2} /></Form.Item>
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item name="content_type" label="Content Type" rules={[{ required: true }]}>
                <Select options={CONTENT_TYPES} />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="schedule_type" label="Schedule Type" rules={[{ required: true }]}>
                <Select options={SCHEDULE_TYPES} />
              </Form.Item>
            </Col>
          </Row>
          {/* Content-type-specific config */}
          {contentType === 'ai_video' && (
            <Card size="small" title="VideoAI Config" style={{ marginBottom: 16, background: '#fafafa' }}>
              <Form.Item name="source_video_id" label="Source Video" rules={[{ required: true, message: 'Please select a source video' }]}>
                <Select placeholder="Select source video" showSearch optionFilterProp="label"
                  options={sourceVideos.map(v => ({ value: v.id, label: `#${v.id} ${v.description || v.original_filename}` }))} />
              </Form.Item>
              <Form.Item name="video_text" label="Script Text" rules={[{ required: true, message: 'Please enter script text' }]}>
                <TextArea rows={3} placeholder="Enter the text for the AI video narration" />
              </Form.Item>
              <Form.Item name="video_voice" label="Voice">
                <Select placeholder="Default: 台灣中文 (女性-曉雨)" allowClear
                  options={Array.isArray(voices) ? voices.map(v => ({ value: typeof v === 'string' ? v : v.name, label: typeof v === 'string' ? v : v.name })) : []} />
              </Form.Item>
            </Card>
          )}
          {contentType === 'ai_article' && (
            <Form.Item name="ai_prompt" label="AI Prompt">
              <TextArea rows={2} placeholder="e.g. 寫一篇關於雲端安全的行銷文章" />
            </Form.Item>
          )}
          {contentType === 'email' && (
            <Form.Item name="email_subject" label="Email Subject">
              <Input placeholder="e.g. 產品優惠通知" />
            </Form.Item>
          )}
          {scheduleType === 'once' && (
            <Form.Item name="scheduled_at" label="Run At"><DatePicker showTime style={{ width: '100%' }} /></Form.Item>
          )}
          {scheduleType === 'weekly' && (
            <Form.Item name="cron_day_of_week" label="Day of Week">
              <Select options={DAYS.map((d, i) => ({ value: i, label: d }))} />
            </Form.Item>
          )}
          {scheduleType === 'monthly' && (
            <Form.Item name="cron_day_of_month" label="Day of Month">
              <InputNumber min={1} max={28} style={{ width: '100%' }} />
            </Form.Item>
          )}
          {scheduleType !== 'once' && (
            <Row gutter={16}>
              <Col span={12}><Form.Item name="cron_hour" label="Hour"><InputNumber min={0} max={23} style={{ width: '100%' }} /></Form.Item></Col>
              <Col span={12}><Form.Item name="cron_minute" label="Minute"><InputNumber min={0} max={59} style={{ width: '100%' }} /></Form.Item></Col>
            </Row>
          )}
          <Form.Item name="channels" label="Publish Channels">
            <Select mode="multiple" options={[
              { value: 'blog', label: 'Blog' },
              { value: 'email', label: 'Email' },
              { value: 'facebook', label: 'Facebook' },
            ]} />
          </Form.Item>
          <Row gutter={16}>
            <Col span={8}>
              <Form.Item name="fb_post_enabled" label="Post to FB" valuePropName="checked"><Switch /></Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item name="fb_ad_enabled" label="FB Ad" valuePropName="checked"><Switch /></Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item name="fb_ad_budget" label="Ad Budget (NT$)"><InputNumber min={0} style={{ width: '100%' }} /></Form.Item>
            </Col>
          </Row>
        </Form>
      </Modal>

      {/* History Modal */}
      <Modal title="Execution History" open={!!historyModal}
        onCancel={() => setHistoryModal(null)} footer={null} width={600}>
        {history.length === 0 ? <Text type="secondary">No executions yet</Text> : (
          <Timeline items={history.map(h => ({
            color: h.status === 'success' ? 'green' : h.status === 'failed' ? 'red' : 'blue',
            children: (
              <div>
                <Text strong>{h.status}</Text>
                <Text type="secondary" style={{ marginLeft: 8 }}>
                  {dayjs(h.executed_at).format('YYYY-MM-DD HH:mm')}
                  {h.duration_ms ? ` (${(h.duration_ms / 1000).toFixed(1)}s)` : ''}
                </Text>
                {h.error && <div><Text type="danger">{h.error}</Text></div>}
                {h.results && (
                  <div style={{ fontSize: 12, color: '#666' }}>
                    {Object.entries(h.results).map(([k, v]) => (
                      <Tag key={k} color={v.ok ? 'green' : 'red'}>{k}: {v.ok ? 'OK' : v.error}</Tag>
                    ))}
                  </div>
                )}
              </div>
            ),
          }))} />
        )}
      </Modal>

      {/* Execution Detail Modal */}
      <Modal title="Execution Detail" open={!!detailModal}
        onCancel={() => setDetailModal(null)} footer={null} width={720}>
        {detailModal && (
          <div>
            <Descriptions bordered size="small" column={2} style={{ marginBottom: 16 }}>
              <Descriptions.Item label="Schedule">{detailModal.schedule_name}</Descriptions.Item>
              <Descriptions.Item label="Type">{detailModal.content_type}</Descriptions.Item>
              <Descriptions.Item label="Status"><ExecStatusTag status={detailModal.status} /></Descriptions.Item>
              <Descriptions.Item label="Duration">
                {detailModal.duration_ms != null ? `${(detailModal.duration_ms / 1000).toFixed(1)}s` : '-'}
              </Descriptions.Item>
              <Descriptions.Item label="Executed At">
                {detailModal.executed_at ? dayjs(detailModal.executed_at).format('YYYY-MM-DD HH:mm:ss') : '-'}
              </Descriptions.Item>
              <Descriptions.Item label="Channels">
                {(detailModal.channels || []).map(c => <Tag key={c}>{c}</Tag>)}
              </Descriptions.Item>
            </Descriptions>

            {detailModal.error && (
              <Card size="small" style={{ marginBottom: 16, background: '#fff2f0', border: '1px solid #ffccc7' }}>
                <Text type="danger"><CloseCircleOutlined style={{ marginRight: 8 }} />{detailModal.error}</Text>
              </Card>
            )}

            {/* Step Progress */}
            {detailModal.progress?.steps?.length > 0 && (
              <Card size="small" title="Steps" style={{ marginBottom: 16 }}>
                <Timeline items={detailModal.progress.steps.map((s, i) => ({
                  color: s.status === 'ok' ? 'green' : s.status === 'fail' ? 'red' : 'blue',
                  dot: s.status === 'running' ? <SyncOutlined spin /> :
                       s.status === 'ok' ? <CheckCircleOutlined /> :
                       s.status === 'fail' ? <CloseCircleOutlined style={{ color: '#ff4d4f' }} /> : undefined,
                  children: (
                    <div>
                      <Text strong>{STEP_LABELS[s.name] || s.name}</Text>
                      {s.detail && <Text type="secondary" style={{ marginLeft: 8 }}>{s.detail}</Text>}
                      <br />
                      <Text type="secondary" style={{ fontSize: 11 }}>
                        {s.ts ? dayjs(s.ts).format('HH:mm:ss') : ''}
                      </Text>
                    </div>
                  ),
                }))} />
              </Card>
            )}

            {/* Results */}
            {detailModal.results && Object.keys(detailModal.results).length > 0 && (
              <Card size="small" title="Execution Results" style={{ marginBottom: 16 }}>
                {Object.entries(detailModal.results).map(([channel, result]) => {
                  const isVideo = channel === 'content' && result?.type === 'ai_video';
                  return (
                    <div key={channel} style={{ marginBottom: 8, padding: '8px 12px', background: result?.ok ? '#f6ffed' : '#fff2f0', borderRadius: 6 }}>
                      <Space>
                        {result?.ok ? <CheckCircleOutlined style={{ color: '#52c41a' }} /> : <CloseCircleOutlined style={{ color: '#ff4d4f' }} />}
                        <Text strong>{channel === 'content' ? `Content (${result?.type || '?'})` : channel}</Text>
                        {isVideo && result?.job_status && <Tag color={result.job_status === 'completed' ? 'green' : result.job_status === 'failed' ? 'red' : 'blue'}>{result.job_status}</Tag>}
                      </Space>
                      <div style={{ fontSize: 12, color: '#666', marginTop: 4 }}>
                        {isVideo ? (
                          <>
                            {result.title && <div>Title: {result.title}</div>}
                            {result.video_url ? (
                              <div style={{ marginTop: 4 }}>
                                <VideoCameraOutlined style={{ marginRight: 4 }} />
                                Video: <a href={result.video_url} target="_blank" rel="noreferrer">{result.video_url}</a>
                              </div>
                            ) : (
                              <div style={{ color: '#fa8c16', marginTop: 4 }}>
                                <ExclamationCircleOutlined style={{ marginRight: 4 }} />
                                {!result.job_id
                                  ? 'No video generated — source_video_id or text not configured. Edit the schedule and set VideoAI Config.'
                                  : result.job_status === 'processing' ? 'Video still processing...' : 'Video generation did not produce a URL'}
                              </div>
                            )}
                            {result.job_id && <div>Job ID: {result.job_id}</div>}
                          </>
                        ) : result?.ok ? (
                          Object.entries(result).filter(([k]) => k !== 'ok').map(([k, v]) => {
                            if (k === 'video_url' && v) return <div key={k}><VideoCameraOutlined style={{ marginRight: 4 }} />Video: <a href={v} target="_blank" rel="noreferrer">{v}</a></div>;
                            if (k === 'post_url' && v) return <div key={k}>Post: <a href={v} target="_blank" rel="noreferrer">{v}</a></div>;
                            if (k === 'slug' && v) return <div key={k}>Blog: <a href={`/blog/${v}`} target="_blank" rel="noreferrer">/blog/{v}</a></div>;
                            return <span key={k}>{k}: {String(v)}{' '}</span>;
                          })
                        ) : (
                          <Text type="danger">{result?.error || 'Failed'}</Text>
                        )}
                      </div>
                    </div>
                  );
                })}
              </Card>
            )}

            {/* Log Entries */}
            {detailModal.log_entries?.length > 0 && (
              <Card size="small" title="Log">
                <div style={{ maxHeight: 300, overflow: 'auto', fontFamily: 'monospace', fontSize: 12 }}>
                  {detailModal.log_entries.map((entry, i) => (
                    <div key={i} style={{
                      padding: '2px 0',
                      color: entry.level === 'error' ? '#ff4d4f' : entry.level === 'warn' ? '#fa8c16' : '#333',
                    }}>
                      <Text type="secondary">{entry.ts ? dayjs(entry.ts).format('HH:mm:ss') : ''}</Text>
                      {' '}
                      <Tag color={entry.level === 'error' ? 'red' : entry.level === 'warn' ? 'orange' : 'blue'}
                        style={{ fontSize: 10, lineHeight: '16px', padding: '0 4px' }}>
                        {entry.level}
                      </Tag>
                      {' '}{entry.msg}
                    </div>
                  ))}
                </div>
              </Card>
            )}
          </div>
        )}
      </Modal>

      {/* AI Suggest Modal */}
      <Modal title={<span><BulbOutlined /> AI Marketing Suggestion</span>} open={aiModal}
        onCancel={() => setAiModal(false)} footer={null} width={640}>
        <Form form={aiForm} layout="vertical">
          <Form.Item name="goal" label="Marketing Goal" rules={[{ required: true }]}>
            <TextArea rows={2} placeholder="e.g. 推廣 AlayaEss 新功能給科技業客戶" />
          </Form.Item>
          <Row gutter={16}>
            <Col span={12}><Form.Item name="target_audience" label="Target Audience"><Input placeholder="e.g. 台灣科技業 CTO" /></Form.Item></Col>
            <Col span={12}><Form.Item name="budget" label="Budget (NT$)"><InputNumber style={{ width: '100%' }} /></Form.Item></Col>
          </Row>
          <Button type="primary" icon={<ThunderboltOutlined />} onClick={handleAiSuggest} loading={aiLoading} block>
            Get AI Suggestion
          </Button>
        </Form>
        {aiSuggestion && (
          <Card style={{ marginTop: 16, background: '#f6ffed', borderRadius: 8 }}>
            <Paragraph style={{ whiteSpace: 'pre-wrap' }}>{aiSuggestion}</Paragraph>
          </Card>
        )}
      </Modal>
    </div>
  );
}
